import React, { useCallback, useState } from "react";
import InputFactory from "../../../../components/InputFactory";
import DialogNote from "./DialogNote";
import { dialog } from "../../../../nq-component";

function DialogCreate({
  schema,
  schemas,
  objects,
  onChangeData,
  handleSubmit,
  handleClose,
}) {
  const extractTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  const extractDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const [formData, setFormData] = useState({
    id: objects?.id || null,
    name: objects?.name || "",
    description: objects?.description || "",
    recruiter: objects?.recruiter || "",
    assignedDate: objects?.assignedDate || null,
    onBoardStartTime: objects?.onBoardStartTime || null,
    onBoardEndTime: objects?.onBoardEndTime || null,
  });
  console.log(formData);
  const [invalidFields, setInvalidFields] = useState([]);

  const handleChange = useCallback(
    (value, field) => {
      setFormData((prevData) => ({
        ...prevData,
        [field]: value,
      }));

      onChangeData(value, field);

      setInvalidFields((prevInvalidFields) =>
        prevInvalidFields.filter((invalidField) => invalidField !== field)
      );
    },
    [onChangeData]
  );

  const combineDateTime = (date, time) => {
    if (!date || !time) return null;

    const [year, month, day] = new Date(date)
      .toISOString()
      .split("T")[0]
      .split("-");
    const [hours, minutes] = time.split(":");

    const localDate = new Date(
      `${year}-${month}-${day}T${hours}:${minutes}:00`
    );

    return localDate.toISOString();
  };

  const validateAndSubmit = () => {
    const requiredFields = [
      "name",
      "recruiter",
      "assignedDate",
      "onBoardStartTime",
      "onBoardEndTime",
    ];
    const missingFields = requiredFields.filter(
      (field) =>
        !formData?.[field] ||
        (typeof formData[field] === "string" && formData[field].trim() === "")
    );

    const startTime = new Date(formData.onBoardStartTime);
    const endTime = new Date(formData.onBoardEndTime);

    if (startTime > endTime) {
      dialog.fire({
        html: (
          <DialogNote
            title="Invalid Time"
            message="End time cannot be earlier than start time."
          />
        ),
        footer: false,
      });
      return;
    }

    if (missingFields.length > 0) {
      setInvalidFields(missingFields);
      dialog.fire({
        html: (
          <DialogNote missingFields={missingFields} title="Missing Fields" />
        ),
        footer: false,
      });
      return;
    }

    handleSubmit(formData);
  };

  const minDate = new Date().toISOString().split("T")[0];

  return (
    <>
      <div
        className="p-4 rounded"
        style={{ maxWidth: "450px", margin: "auto" }}
      >
        <div
          className="d-flex align-items-center mb-3"
          style={{ color: "#d18237" }}
        >
          <div
            className="bg-light p-3 rounded-circle d-flex justify-content-center align-items-center"
            style={{ width: "50px", height: "50px" }}
          >
            <i className="bi bi-plus fs-4"></i>
          </div>
          <h5 className="fw-bold ms-2">Onboarding Form</h5>
        </div>
        <div className="mb-3">
          <label className="form-label fw-bold">
            Name<span className="text-danger fw-lighter ms-1">*</span>
          </label>
          <InputFactory
            type="String"
            field="name"
            onChange={(value, field) => handleChange(value, field)}
            placeholder="Enter Name"
            defaultValue={formData.name}
            className={invalidFields.includes("name") ? "is-invalid" : ""}
          />
        </div>
        <div className="mb-3">
          <label className="form-label fw-bold">Description</label>
          <InputFactory
            type="String"
            field="description"
            onChange={(value, field) => handleChange(value, field)}
            placeholder="Enter Description (Optional)"
            defaultValue={formData.description}
          />
        </div>
        <div className="mb-3">
          <label className="form-label fw-bold">
            Select Assigned
            <span className="text-danger fw-lighter ms-1">*</span>
          </label>
          <InputFactory
            type="Pointer"
            _type="RecruiterPointer"
            target="users"
            field="recruiter"
            schemas={schemas}
            onChange={(value, field) => handleChange(value, field)}
            placeholder="Select Recruiter"
            recruiter={"Recruiter"}
            required
            defaultValue={formData.recruiter}
            className={invalidFields.includes("recruiter") ? "is-invalid" : ""}
          />
        </div>
        <div className="mb-3">
          <label className="form-label fw-bold">
            Date Assigned<span className="text-danger fw-lighter ms-1">*</span>
          </label>
          <InputFactory
            type="Date"
            field="assignedDate"
            onChange={(value) => handleChange(value, "assignedDate")}
            defaultValue={extractDate(formData.assignedDate)}
            min={minDate}
            className={
              invalidFields.includes("assignedDate") ? "is-invalid" : ""
            }
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">
            Start Time
            <span className="text-danger fw-lighter ms-1">*</span>
          </label>
          <InputFactory
            type="Time"
            field="onBoardStartTime"
            onChange={(time) => {
              const datetime = combineDateTime(formData.assignedDate, time);
              handleChange(datetime, "onBoardStartTime");
            }}
            defaultValue={extractTime(formData.onBoardStartTime)}
            className={
              invalidFields.includes("onBoardStartTime") ? "is-invalid" : ""
            }
          />
        </div>
        <div className="mb-3">
          <label className="form-label fw-bold">
            End Time
            <span className="text-danger fw-lighter ms-1">*</span>
          </label>
          <InputFactory
            type="Time"
            field="onBoardEndTime"
            onChange={(time) => {
              const datetime = combineDateTime(formData.assignedDate, time);
              handleChange(datetime, "onBoardEndTime");
            }}
            defaultValue={extractTime(formData.onBoardEndTime)}
            className={
              invalidFields.includes("onBoardEndTime") ? "is-invalid" : ""
            }
          />
        </div>

        <div className="d-flex justify-content-center">
          <button
            className="btn text-white px-4 me-2"
            style={{ backgroundColor: "#d18237" }}
            onClick={validateAndSubmit}
          >
            {objects?.id ? "Update" : "Confirm"}
          </button>
          <button
            className="btn px-4"
            onClick={handleClose}
            style={{ backgroundColor: "#d0d0d0" }}
          >
            Cancel
          </button>
        </div>
      </div>
    </>
  );
}

export default DialogCreate;
